import { call, put, select } from 'redux-saga/effects';
import {
    getLiteTradeAccountPortfolio,
    getLiteTradeConfig,
    getLiteTradeSessionToken,
} from '../../../../modules/LiteTrade/liteTradeSelectors';
import { getIsAppVisibility, getUserAccounts } from '../../../../app/appSelectors';
import { oppositeSubscriptionName, SOCKET_TYPE_MESSAGES } from '../../../../mobileAppCore/mobileAppConstants';
import { IMobileAppRootState } from '../../../../mobileAppCore/types/mobileAppState.t';
import {
    processLiteTradeOrderNotificationAction,
    processLiteTradePositionNotificationAction,
    processLiteTradeRejectedOrderNotificationAction,
    setLiteTradeAccountPortfolio,
    updatePositionPlLastSeenSignByPortfolioAction,
} from '../../../../modules/LiteTrade/liteTradeActions';
import { websocketOpenSubscriptionById } from '../../websocketReducer';
import _ from 'lodash';
import {
    LITE_TRADE_DX_REJECT_CODES,
    LITE_TRADE_ORDER_STATUSES,
    MAP_LITE_TRADE_DX_REJECT_SUBSTRING_TO_REJECT_CODE,
} from '../../../../modules/LiteTrade/liteTradeConstants';
import { ORDER_TYPES } from '../../../../components/OrderTypeSelector/orderTypeSelectorConstants';
import {
    LITE_TRADE_NOTIFICATION_STATUSES,
} from '../../../../modules/LiteTrade/components/LiteTradeNotification/liteTradeNotificationConstants';
import { getWebsocketSubscriptionById } from '../../websocketSelectors';
import LiteTradeNotificationTopUp
    from '../../../../modules/LiteTrade/components/LiteTradeNotification/LiteTradeNotificationTopUp';
import { logAppsflyerAction } from '../../../../app/appActions';
import { LOG_AF_EVENT_NAMES } from '../../../../app/appConstants';
import parseAccountName from '../../../../utils/parseAccountName';
import { ILiteTradePortfolioOrder } from '../../../../modules/LiteTrade/types/liteTradeReducer.t';
import calcAssetFilledQuantity from '../../../../modules/LiteTrade/utils/calcAssetFilledQuantity';
import { makeSelectLangPath } from '../../../LangProvider/langProviderSelectors';

const findLiteTradeRejectCode = (rejectReason: any) =>
    Object.entries(MAP_LITE_TRADE_DX_REJECT_SUBSTRING_TO_REJECT_CODE).find(([rejectSubstring]) =>
        rejectReason.includes(rejectSubstring),
    )?.[1] || '';

function* calcPortfolioOrdersFilledQuantitySaga({ payload }: { payload: { orders: ILiteTradePortfolioOrder[] } }) {
    const { orders } = payload;
    const portfolio = yield select(getLiteTradeAccountPortfolio);
    const processedCompletedPositionsCodesMap = {};
    const mapOrderCodesToFilledQuantity = {};

    orders.forEach((order) => {
        const symbol = order.instrument;
        const positionCode = order?.legs[0]?.positionCode;
        if (order.status === LITE_TRADE_ORDER_STATUSES.COMPLETED && order.type === ORDER_TYPES.MARKET) {
            processedCompletedPositionsCodesMap[positionCode] = true;
        }
        mapOrderCodesToFilledQuantity[order.orderCode] = calcAssetFilledQuantity(
            symbol,
            portfolio.positions?.filter(item => !processedCompletedPositionsCodesMap[item.positionCode])
        );
    });

    return mapOrderCodesToFilledQuantity;
}

function* processPortfolioNotificationsSaga(portfolio) {
    const currentClosedPositionCodes = _.chain(portfolio.orders)
        .filter({ status: LITE_TRADE_ORDER_STATUSES.COMPLETED, type: ORDER_TYPES.MARKET })
        .map((order) => order.legs[0]?.positionCode)
        .value();

    const mapOrderCodesToFilledQuantity = yield call(calcPortfolioOrdersFilledQuantitySaga, { payload: { orders: portfolio.orders } });

    const orders = portfolio.orders;
    for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        if (order.status === LITE_TRADE_ORDER_STATUSES.COMPLETED) {
            yield put(processLiteTradePositionNotificationAction({ portfolio, order, filledQuantity: mapOrderCodesToFilledQuantity[order.orderCode] }));
        } else if (order.status === LITE_TRADE_ORDER_STATUSES.REJECTED) {
            const execution =
                _.find(order.executions, {
                    finalStatus: true,
                    status: LITE_TRADE_ORDER_STATUSES.REJECTED,
                }) || {};
            const rejectCode = findLiteTradeRejectCode(execution.rejectReason);
            let description,
                notificationChildren,
                notificationStatus,
                autoClose: number | boolean = true;

            if (rejectCode) {
                description = yield select(makeSelectLangPath(
                    `lite_version.notifications.reject_reason.${rejectCode}`,
                    { symbol: order.instrument }
                ));
                if (rejectCode === LITE_TRADE_DX_REJECT_CODES.NOT_ENOUGH_SPACE) {
                    notificationChildren = <LiteTradeNotificationTopUp />;
                    notificationStatus = LITE_TRADE_NOTIFICATION_STATUSES.ATTENTION;
                    autoClose = 5000;
                }
            }

            yield put(
                processLiteTradeRejectedOrderNotificationAction({
                    autoClose,
                    portfolio,
                    order,
                    description,
                    notificationChildren,
                    notificationStatus,
                    filledQuantity: mapOrderCodesToFilledQuantity[order.orderCode],
                }),
            );
            yield put(
                logAppsflyerAction({
                    name: LOG_AF_EVENT_NAMES.ORDER_ERROR,
                    payload: {
                        account_type: parseAccountName(order.account)?.type,
                        asset: order.instrument,
                        error_code: rejectCode,
                        error_message: description,
                    },
                }),
            );
        } else if ([LITE_TRADE_ORDER_STATUSES.CANCELED, LITE_TRADE_ORDER_STATUSES.WORKING].includes(order.status)) {
            // не показываем уведомления для ордеров если закрыли вместе с позицией
            if (
                order.status !== LITE_TRADE_ORDER_STATUSES.CANCELED ||
                !currentClosedPositionCodes.includes(order.legs[0]?.positionCode)
            ) {
                yield put(processLiteTradeOrderNotificationAction({ portfolio, order, filledQuantity: mapOrderCodesToFilledQuantity[order.orderCode] }));
            }
        }
    }
}

function* dispatchAccountPortfolioSaga(portfolio) {
    yield call(processPortfolioNotificationsSaga, portfolio);
    yield put(setLiteTradeAccountPortfolio(portfolio));
    yield put(updatePositionPlLastSeenSignByPortfolioAction(portfolio));
}

function* subscriptionAccountPortfoliosSaga() {
    const liteTradeConfig = yield select(getLiteTradeConfig);
    const channel = liteTradeConfig?.wssUrl;
    const requestId = `_${SOCKET_TYPE_MESSAGES.ACCOUNT_PORTFOLIOS_OPEN}`;
    const type = SOCKET_TYPE_MESSAGES.ACCOUNT_PORTFOLIOS_OPEN;
    const accounts = yield select(getUserAccounts);
    const sessionToken = yield select(getLiteTradeSessionToken);
    const isAppVisibility = yield select(getIsAppVisibility);
    const lastSubscription = yield select(getWebsocketSubscriptionById(channel, requestId));

    if (lastSubscription?.prev?.accounts && (lastSubscription?.state === 'open' || lastSubscription?.state === 'opening')) {
        const prevAccounts = lastSubscription.prev.accounts;
        if (_.isEqual(prevAccounts, accounts)) {
            return;
        }
    }

    if (accounts.length && isAppVisibility && sessionToken) {
        yield put(
            websocketOpenSubscriptionById({
                id: requestId,
                channel,
                prev: {
                    accounts,
                },
                open: function (state: IMobileAppRootState) {
                    const sessionToken = getLiteTradeSessionToken(state);
                    const accounts = getUserAccounts(state);
                    const accountCodes = accounts.map((item) => item.name);

                    return {
                        requestId,
                        type,
                        payload: {
                            accounts: accountCodes.map((accountCode) => `default:${accountCode}`),
                        },
                        session: sessionToken,
                    };
                },
                close: function (state: IMobileAppRootState) {
                    const sessionToken = getLiteTradeSessionToken(state);
                    return {
                        type: oppositeSubscriptionName[type],
                        refRequestId: requestId,
                        session: sessionToken,
                    };
                },
                process: function* (messageData: ReturnType<typeof JSON.parse>) {
                    const portfolios = messageData?.payload?.portfolios;
                    if (portfolios instanceof Array)
                        for (let i = 0; i < portfolios.length; i++) {
                            yield call(dispatchAccountPortfolioSaga, portfolios[i]);
                        }
                },
            }),
        );
    }
}

export default subscriptionAccountPortfoliosSaga;
